<template>
    <div class="glass-container">
        <div class="locations-management-container">
            <!-- Header Section -->
            <div class="modern-card">
                <div class="header-section">
                    <div class="title-section">
                        <h1 class="page-title">Locations Management</h1>
                        <p class="page-subtitle">Manage business locations and sub-locations</p>
                    </div>
                    
                    <div class="action-buttons">
                        <b-button @click="add_new" variant="primary" class="action-btn add-btn">
                            <b-icon icon="plus-circle" class="mr-2"></b-icon>
                            Add Location
                        </b-button>
                        <b-button @click="fetch_init" variant="outline-secondary" class="action-btn refresh-btn">
                            <b-icon icon="arrow-clockwise" class="mr-2"></b-icon>
                            Refresh
                        </b-button>
                    </div>
                </div>

                <!-- Loading State -->
                <div v-if="items_being_loaded" class="loading-container">
                    <loading 
                        :active="items_being_loaded"
                        :can-cancel="false"
                        :is-full-page="false"
                    ></loading>
                </div>

                <!-- Locations List -->
                <div v-else class="locations-list-container">
                    <div v-if="items.length > 0">
                        <div v-for="(item, idx) in items" :key="idx" class="location-card">
                            <!-- Location Header -->
                            <div class="location-header">
                                <div class="location-id">
                                    <span class="field-label">ID</span>
                                    <b-input 
                                        readonly 
                                        v-model="item.location_id" 
                                        size="sm" 
                                        class="id-input"
                                        :class="{ 'new-item': !item.location_id }"
                                    ></b-input>
                                </div>
                                
                                <div class="location-name">
                                    <span class="field-label">NAME</span>
                                    <b-input 
                                        v-model="item.location_name" 
                                        size="sm" 
                                        class="name-input"
                                        placeholder="Enter location name"
                                    ></b-input>
                                </div>
                                
                                <div class="location-status-badge" v-if="item.is_main_warehouse">
                                    <span class="badge badge-primary">MAIN WAREHOUSE</span>
                                </div>
                            </div>

                            <!-- Location Details Row -->
                            <div class="location-details-row">
                                <!-- Address -->
                                <div class="detail-field">
                                    <span class="field-label">ADDRESS</span>
                                    <b-input 
                                        v-model="item.location_address" 
                                        size="sm" 
                                        class="address-input"
                                        placeholder="Enter address"
                                    ></b-input>
                                </div>
                                
                                <!-- Zipcode -->
                                <div class="detail-field">
                                    <span class="field-label">ZIPCODE</span>
                                    <b-input 
                                        v-model="item.location_zipcode" 
                                        size="sm" 
                                        class="zipcode-input"
                                        placeholder="Zipcode"
                                    ></b-input>
                                </div>
                                
                                <!-- Warehouse -->
                                <div class="detail-field">
                                    <span class="field-label">WAREHOUSE</span>
                                    <b-form-select 
                                        size="sm" 
                                        v-model="item.is_main_warehouse" 
                                        :options="is_warehouse_options"
                                        class="warehouse-select"
                                        :class="{ 'is-warehouse': item.is_main_warehouse }"
                                    ></b-form-select>
                                </div>
                                
                                <!-- Priority -->
                                <div class="detail-field">
                                    <span class="field-label">PRIORITY</span>
                                    <b-form-select 
                                        size="sm" 
                                        v-model="item.priority" 
                                        :options="priority_options"
                                        class="priority-select"
                                    ></b-form-select>
                                </div>
                            </div>

                            <!-- Additional Details Row -->
                            <div class="location-details-row">
                                <!-- Short Name -->
                                <div class="detail-field">
                                    <span class="field-label">SHORT NAME</span>
                                    <b-input 
                                        v-model="item.location_short_name" 
                                        size="sm" 
                                        class="short-name-input"
                                        placeholder="Short name"
                                    ></b-input>
                                </div>
                                
                                <!-- Description -->
                                <div class="detail-field wide-field">
                                    <span class="field-label">DESCRIPTION</span>
                                    <b-input 
                                        v-model="item.location_description" 
                                        size="sm" 
                                        class="description-input"
                                        placeholder="Description"
                                    ></b-input>
                                </div>
                                
                                <!-- Email -->
                                <div class="detail-field">
                                    <span class="field-label">EMAIL</span>
                                    <b-input 
                                        v-model="item.location_email" 
                                        size="sm" 
                                        class="email-input"
                                        placeholder="email@example.com"
                                    ></b-input>
                                </div>
                            </div>

                            <!-- Contact & Tax Row -->
                            <div class="location-details-row">
                                <!-- Phone -->
                                <div class="detail-field">
                                    <span class="field-label">PHONE</span>
                                    <b-input 
                                        v-model="item.location_phone" 
                                        size="sm" 
                                        class="phone-input"
                                        placeholder="Phone number"
                                    ></b-input>
                                </div>
                                
                                <!-- Sales Tax -->
                                <div class="detail-field">
                                    <span class="field-label">SALES TAX</span>
                                    <b-input 
                                        v-model="item.sales_tax" 
                                        size="sm" 
                                        class="tax-input"
                                        type="number"
                                        step="0.01"
                                    ></b-input>
                                </div>
                                
                                <!-- State -->
                                <div class="detail-field">
                                    <span class="field-label">STATE</span>
                                    <b-form-select 
                                        size="sm"
                                        v-model="item.state" 
                                        :options="states"
                                        class="state-select"
                                    ></b-form-select>
                                </div>
                            </div>

                            <!-- Action Buttons -->
                            <div class="location-actions">
                                <b-button 
                                    @click="show_sublocations(item)" 
                                    variant="outline-info" 
                                    size="sm"
                                    class="action-btn sublocation-btn"
                                >
                                    <b-icon :icon="item.show_sublocations ? 'chevron-up' : 'chevron-down'" class="mr-1"></b-icon>
                                    Sublocations ({{ item.sublocations.length }})
                                </b-button>
                                
                                <b-button 
                                    @click="save_location(item)" 
                                    variant="success" 
                                    size="sm"
                                    class="action-btn save-btn"
                                    :disabled="!item.location_name"
                                >
                                    <b-icon icon="check" class="mr-1"></b-icon>
                                    Save
                                </b-button>
                                
                                <b-button 
                                    @click="reset_inventory(item)" 
                                    variant="outline-warning" 
                                    size="sm"
                                    class="action-btn reset-btn"
                                    v-if="item.location_id"
                                >
                                    <b-icon icon="arrow-counterclockwise" class="mr-1"></b-icon>
                                    Reset Inventory
                                </b-button>
                                
                                <b-button 
                                    @click="remove_location(idx)" 
                                    variant="outline-danger" 
                                    size="sm"
                                    class="action-btn cancel-btn"
                                    v-if="!item.location_id"
                                >
                                    <b-icon icon="x" class="mr-1"></b-icon>
                                    Cancel
                                </b-button>

                                <!-- Audit Trail Button -->
                                <b-button 
                                    @click="view_audit_trail(item.location_id)" 
                                    variant="outline-secondary" 
                                    size="sm"
                                    class="action-btn audit-btn"
                                    v-if="item.location_id"
                                >
                                    <b-icon icon="clock-history" class="mr-1"></b-icon>
                                    Audit Trail
                                </b-button>
                            </div>

                            <!-- Sublocations Section -->
                            <b-collapse v-model="item.show_sublocations" class="sublocations-section">
                                <div class="sublocations-container">
                                    <div class="sublocations-header">
                                        <h6 class="sublocations-title">Sublocations</h6>
                                        <b-button 
                                            @click="add_new_sublocation(item)" 
                                            variant="outline-primary" 
                                            size="sm"
                                            class="add-sublocation-btn"
                                        >
                                            <b-icon icon="plus" class="mr-1"></b-icon>
                                            Add Sublocation
                                        </b-button>
                                    </div>
                                    
                                    <div v-if="item.sublocations.length > 0" class="sublocations-list">
                                        <div v-for="(sublocation, sidx) in item.sublocations" :key="sidx" class="sublocation-item">
                                            <div class="sublocation-number">
                                                <span class="sublocation-index">#{{ sidx + 1 }}</span>
                                            </div>
                                            
                                            <div class="sublocation-name">
                                                <b-input 
                                                    v-model="sublocation.sublocation_name" 
                                                    size="sm" 
                                                    placeholder="Sublocation name"
                                                    class="sublocation-name-input"
                                                ></b-input>
                                            </div>
                                            
                                            <div class="sublocation-type">
                                                <b-form-select 
                                                    v-model="sublocation.sublocation_type_id" 
                                                    :options="sublocation_type_ids"
                                                    size="sm"
                                                    class="sublocation-type-select"
                                                ></b-form-select>
                                            </div>
                                            
                                            <div class="sublocation-actions">
                                                <b-button 
                                                    @click="save_sublocation(item, sublocation)" 
                                                    variant="success" 
                                                    size="sm"
                                                    class="save-sublocation-btn"
                                                    :disabled="!sublocation.sublocation_name"
                                                >
                                                    Save
                                                </b-button>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div v-else class="no-sublocations">
                                        <p class="text-muted">No sublocations added yet.</p>
                                    </div>
                                </div>
                            </b-collapse>
                        </div>
                        
                        <!-- Locations Count -->
                        <div class="table-footer">
                            <div class="pagination-info">
                                Showing {{ items.length }} locations
                            </div>
                        </div>
                    </div>
                    
                    <!-- Empty State -->
                    <div v-else class="empty-state">
                        <div class="empty-state-icon">
                            <b-icon icon="building" font-scale="3"></b-icon>
                        </div>
                        <h3>No Locations Found</h3>
                        <p>No locations in the system yet</p>
                        <div class="empty-state-actions mt-3">
                            <b-button @click="add_new" variant="primary">
                                <b-icon icon="plus-circle" class="mr-2"></b-icon>
                                Add First Location
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Audit Trail Modal -->
        <b-modal 
            id="audit-trail-modal" 
            title="Location Audit Trail" 
            size="lg"
            scrollable
            @hidden="show_audit_trail = false"
        >
            <div v-if="audit_trail_loading" class="text-center py-4">
                <b-spinner variant="primary" label="Loading..."></b-spinner>
                <p class="mt-2">Loading audit trail...</p>
            </div>
            <div v-else-if="audit_trail.length === 0" class="text-center py-4">
                <b-icon icon="inbox" font-scale="3" class="text-muted mb-3"></b-icon>
                <p class="text-muted">No audit records found for this location.</p>
            </div>
            <div v-else>
                <div class="audit-summary mb-3">
                    <span class="badge badge-info">Total: {{ audit_trail.length }} records</span>
                </div>
                <div class="audit-timeline">
                    <div 
                        v-for="(record, index) in audit_trail" 
                        :key="index" 
                        class="audit-item"
                    >
                        <div class="audit-icon">
                            <b-icon 
                                :icon="get_audit_icon(record.operation_type)" 
                                :variant="get_audit_variant(record.operation_type)"
                                font-scale="1.5"
                            ></b-icon>
                        </div>
                        <div class="audit-content">
                            <div class="audit-header">
                                <span class="audit-operation">
                                    <strong>{{ record.operation_type }}</strong>
                                </span>
                                <span class="audit-user">
                                    <b-icon icon="person" font-scale="0.8"></b-icon>
                                    {{ record.user_name || 'System' }}
                                    <span class="text-muted" v-if="record.user_role">
                                        ({{ record.user_role }})
                                    </span>
                                </span>
                                <span class="audit-time">
                                    <b-icon icon="clock" font-scale="0.8"></b-icon>
                                    {{ record.operation_timestamp }}
                                </span>
                            </div>
                            <div class="audit-description">
                                {{ record.operation_description || 'No description' }}
                            </div>
                            <div v-if="record.changed_fields && record.changed_fields !== '{}' && record.changed_fields !== null" class="audit-changes">
                                <small class="text-muted">Changes:</small>
                                <ul class="change-list">
                                    <li v-for="(change, key) in parse_changed_fields(record.changed_fields)" :key="key">
                                        <strong>{{ format_field_name(key) }}:</strong> 
                                        <span class="old-value">{{ change.old || 'null' }}</span>
                                        <b-icon icon="arrow-right" font-scale="0.8"></b-icon>
                                        <span class="new-value">{{ change.new || 'null' }}</span>
                                    </li>
                                </ul>
                            </div>
                            <div v-if="record.ip_address" class="audit-ip">
                                <small class="text-muted">
                                    <b-icon icon="wifi" font-scale="0.8"></b-icon>
                                    IP: {{ record.ip_address }}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <template #modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('audit-trail-modal')">
                    Close
                </b-button>
            </template>
        </b-modal>
    </div>
</template>

<script>
    import Requestor from '@/utilities/Requestor'
    import Loading from 'vue-loading-overlay';
    import 'vue-loading-overlay/dist/vue-loading.css';

    export default {
        name: "ListLocations",
        components: { Loading },
        data: function() {
            return {
                packet: {},
                items: [],
                items_being_loaded: false,
                is_warehouse_options: [
                    {value: false, text: "NO"},
                    {value: true, text: "YES"}
                ],
                sublocation_type_ids: [
                    {value: 1, text: "FLOOR"},
                    {value: 2, text: "STOCK"}
                ],
                states: [
                    {value: null, text: ''},
                    {value: 'PA', text: 'PA'},
                    {value: 'NJ', text: 'NJ'}
                ],
                sort_asc: true,
                // Audit Trail Data
                show_audit_trail: false,
                audit_trail: [],
                audit_trail_loading: false,
                selected_location_id: null,
            }
        },
        computed: {
            priority_options() {
                const MAX_PRIORITY = 20;
                const used = new Set(this.items
                    .filter(i => i.priority != null)
                    .map(i => parseInt(i.priority)));

                return Array.from({ length: MAX_PRIORITY }, (v, i) => {
                    const val = i + 1;
                    return {
                        value: val,
                        text: `${val}${val === 1 ? ' (Highest)' : val === MAX_PRIORITY ? ' (Lowest)' : ''}`,
                        disabled: used.has(val)
                    };
                });
            }
        },
        methods: {
            // Helper to get user context
            getUserContext: function() {
                let userDetails = this.packet.userDetails || {};
                return {
                    user_id: userDetails.id || null,
                    username: userDetails.username || 'SYSTEM',
                    user_role: userDetails.role || 'SYSTEM',
                    user_name: userDetails.fullname || userDetails.username || 'SYSTEM',
                    ip_address: this.packet.client_ip || '127.0.0.1',
                    user_agent: navigator.userAgent || 'Unknown'
                };
            },

            reset_inventory: function(item){
                let path = "/api/location/reset_inventory/"
                let userContext = this.getUserContext();
                let data = {
                    ...item,
                    user_id: userContext.user_id,
                    username: userContext.username,
                    user_name: userContext.user_name,
                    user_role: userContext.user_role
                };
                let that = this;
                let s = function(response){
                    if (response.data.error){
                        that.$bvToast.toast(
                            response.data.error, 
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    }
                    else {
                        that.$bvToast.toast(
                            "Inventory reset successfully.", 
                            {title: "Success", autoHideDelay: 1500, variant:"success"}
                        );
                    }
                    that.items_being_loaded = false;
                }
                let f = function(error){
                    console.log(error);
                    that.$bvToast.toast(
                        "Inventory reset failed. Please try again.", 
                        {title: "Error", autoHideDelay: 1500, variant:"warning"}
                    );
                    that.items_being_loaded = false;
                }
                this.items_being_loaded = true
                Requestor.postSyncP(path, data, this, s, f);
            },
            
            remove_location: function(idx){
                this.items.splice(idx, 1);
            },
            
            show_sublocations: function(item){
                item.show_sublocations = !item.show_sublocations;
            },
            
            save_location: function(item){
                let path = "/api/location/save_location/"
                let userContext = this.getUserContext();
                let data = {
                    ...item,
                    user_id: userContext.user_id,
                    username: userContext.username,
                    user_name: userContext.user_name,
                    user_role: userContext.user_role
                };
                let that = this;
                let s = function(response){
                    if (response.data.error){
                        that.$bvToast.toast(
                            response.data.error, 
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    }
                    else {
                        that.$bvToast.toast(
                            "Location saved successfully.", 
                            {title: "Success", autoHideDelay: 1500, variant:"success"}
                        );
                        that.fetch_init();
                    }
                    that.items_being_loaded = false;
                }
                let f = function(error){
                    console.log(error);
                    that.$bvToast.toast(
                        "Error saving location. Please try again.", 
                        {title: "Error", autoHideDelay: 1500, variant:"warning"}
                    );
                    that.items_being_loaded = false;
                }
                this.items_being_loaded = true
                Requestor.postSyncP(path, data, this, s, f);
            },
            
            save_sublocation: function(item, sublocation){
                let path = "/api/location/save_sublocation/"
                let userContext = this.getUserContext();
                let data = {
                    ...sublocation,
                    user_id: userContext.user_id,
                    username: userContext.username,
                    user_name: userContext.user_name,
                    user_role: userContext.user_role
                };
                let that = this;
                let s = function(response){
                    if (response.data.error){
                        that.$bvToast.toast(
                            response.data.error, 
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    }
                    else {
                        that.$bvToast.toast(
                            "Sublocation saved successfully.", 
                            {title: "Success", autoHideDelay: 1500, variant:"success"}
                        );
                        item.show_sublocations = true;
                        that.fetch_init();
                    }
                    that.items_being_loaded = false;
                }
                let f = function(error){
                    console.log(error);
                    that.$bvToast.toast(
                        "Error saving sublocation. Please try again.", 
                        {title: "Error", autoHideDelay: 1500, variant:"warning"}
                    );
                    that.items_being_loaded = false;
                    item.show_sublocations = true;
                }
                this.items_being_loaded = true
                Requestor.postSyncP(path, data, this, s, f);
            },
            
            fetch_init: function(){
                let path = "/api/location/get_all_locations/"
                let data = {}
                let that = this;
                let s = function(response){
                    if (response.data.error){
                        that.$bvToast.toast(
                            response.data.error, 
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    }
                    else {
                        that.items = response.data.result;
                    }
                    that.items_being_loaded = false;
                }
                let f = function(error){
                    console.log(error);
                    that.$bvToast.toast(
                        "Error loading locations. Please refresh.", 
                        {title: "Error", autoHideDelay: 1500, variant:"warning"}
                    );
                    that.items_being_loaded = false;
                }
                this.items_being_loaded = true
                Requestor.getSyncP(path, data, this, s, f);
            },
            
            add_new: function(){
                let item = {
                    location_id: null,
                    location_name: null,
                    location_address: null,
                    is_main_warehouse: false,
                    location_zipcode: null,
                    location_short_name: null,
                    location_ts: null,
                    show_sublocations: false,
                    edit: false,
                    sublocations: [],
                    sales_tax: 8.0,
                    priority: null,
                    state: null,
                    location_description: null,
                    location_email: null,
                    location_phone: null
                }
                this.items.unshift(item);
            },
            
            add_new_sublocation: function(item){
                let val = {
                    location_id: item.location_id,
                    sublocation_name: null,
                    sublocation_type_id: null,
                }
                item.sublocations.unshift(val);
            },
            
            sortby: function(key, mapper){
                if (this.sort_asc){
                    this.items = Requestor.sort(this.items, key, mapper)
                }
                else {
                    this.items = Requestor.sort_reverse(this.items, key, mapper)
                }
                this.sort_asc = !this.sort_asc;
            },

            // Audit Trail Methods
            view_audit_trail: function(location_id) {
                this.audit_trail_loading = true;
                this.audit_trail = [];
                this.selected_location_id = location_id;
                
                let path = "/api/location/get_audit_trail/"
                let params = { location_id: location_id }
                let that = this;
                
                let s = function(response){
                    if (response.data.error){
                        that.$bvToast.toast(
                            response.data.error, 
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    }
                    else {
                        that.audit_trail = response.data.result || [];
                        that.$bvModal.show('audit-trail-modal');
                    }
                    that.audit_trail_loading = false;
                }
                
                let f = function(error){
                    console.log(error);
                    that.$bvToast.toast(
                        "Error loading audit trail. Please try again.", 
                        {title: "Error", autoHideDelay: 1500, variant:"warning"}
                    );
                    that.audit_trail_loading = false;
                }
                
                Requestor.getSyncP(path, params, this, s, f);
            },
            
            get_audit_icon: function(operation_type) {
                const icons = {
                    'INSERT': 'plus-circle',
                    'UPDATE': 'pencil',
                    'DELETE': 'trash'
                };
                return icons[operation_type] || 'info-circle';
            },
            
            get_audit_variant: function(operation_type) {
                const variants = {
                    'INSERT': 'success',
                    'UPDATE': 'warning',
                    'DELETE': 'danger'
                };
                return variants[operation_type] || 'info';
            },
            
            parse_changed_fields: function(changed_fields) {
                if (!changed_fields) return {};
                if (typeof changed_fields === 'string') {
                    try {
                        return JSON.parse(changed_fields);
                    } catch(e) {
                        return {};
                    }
                }
                return changed_fields;
            },
            
            format_field_name: function(field_name) {
                const field_map = {
                    'name': 'Name',
                    'short_name': 'Short Name',
                    'address': 'Address',
                    'zipcode': 'Zipcode',
                    'phone': 'Phone',
                    'email': 'Email',
                    'description': 'Description',
                    'sales_tax': 'Sales Tax',
                    'is_main_warehouse': 'Warehouse',
                    'state': 'State',
                    'priority': 'Priority',
                    'is_warehouse': 'Is Warehouse',
                    'status': 'Status',
                    'sublocation_name': 'Sublocation Name',
                    'sublocation_type_id': 'Sublocation Type'
                };
                return field_map[field_name] || field_name.replace(/_/g, ' ').toUpperCase();
            }
        },
        created: function(){
            this.packet = JSON.parse(localStorage.packet);
            this.packet.userDetails = JSON.parse(localStorage.userDetails);
            this.fetch_init();
        }
    }
</script>

<style scoped>
.glass-container {
    min-height: 100vh;
    padding: 20px;
    background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
    background-size: cover;
    background-position: center;
    background-attachment: fixed;
    position: relative;
    overflow: hidden;
}

.glass-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(2px);
    z-index: 0;
}

.locations-management-container {
    padding: 20px;
    background: transparent;
    min-height: 100vh;
    border-radius: 34px;
    z-index: 1;
    position: relative;
}

.modern-card {
    background: rgba(255, 255, 255, 0.7);
    border-radius: 15px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    padding: 25px;
    margin: 0 auto;
    margin-top: 10px;
    max-width: 1800px;
}

/* Header */
.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 25px;
    padding-bottom: 20px;
    border-bottom: 2px solid #f0f0f0;
}

.title-section .page-title {
    font-size: 1.8rem;
    font-weight: 700;
    color: #2d3748;
    margin: 0;
}

.title-section .page-subtitle {
    color: #718096;
    font-size: 0.95rem;
    margin: 5px 0 0;
}

.action-buttons {
    display: flex;
    gap: 10px;
}

.action-btn {
    padding: 8px 16px;
    font-weight: 600;
    border-radius: 8px;
    transition: all 0.2s ease;
}

.add-btn {
    background: #667eea;
    border: none;
}

.add-btn:hover {
    background: #5a67d8;
    transform: translateY(-1px);
}

.refresh-btn:hover {
    transform: translateY(-1px);
}

/* Loading */
.loading-container {
    text-align: center;
    padding: 40px 20px;
}

/* Location Card */
.location-card {
    background: #ffffff;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    padding: 20px;
    margin-bottom: 15px;
    border: 1px solid #e2e8f0;
    transition: all 0.3s ease;
}

.location-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #cbd5e0;
}

/* Location Header */
.location-header {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 15px;
    padding-bottom: 15px;
    border-bottom: 1px solid #e2e8f0;
}

.location-id {
    min-width: 80px;
}

.id-input {
    background: #f8f9fa;
    border-color: #e2e8f0;
    text-align: center;
    font-weight: bold;
    font-size: 12px;
}

.id-input.new-item {
    background: #fff3cd;
    border-color: #ffeaa7;
}

.location-name {
    flex: 1;
    min-width: 200px;
}

.name-input {
    background: #f8f9fa;
    border-color: #e2e8f0;
    font-size: 12px;
    font-weight: bold;
}

.name-input:focus {
    background: #ffffff;
    border-color: #667eea;
}

.location-status-badge .badge {
    padding: 6px 12px;
    font-size: 11px;
    font-weight: 600;
}

/* Field Label */
.field-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: #718096;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
}

/* Details Rows */
.location-details-row {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    margin-bottom: 15px;
}

.detail-field {
    flex: 1;
    min-width: 150px;
}

.detail-field.wide-field {
    flex: 2;
    min-width: 300px;
}

/* Input Styles */
.address-input, .zipcode-input, .short-name-input,
.description-input, .email-input, .phone-input,
.tax-input {
    background: #f8f9fa;
    border-color: #e2e8f0;
    font-size: 12px;
}

.warehouse-select.is-warehouse {
    background: #fff3cd;
    border-color: #ffeaa7;
    font-weight: bold;
}

.priority-select, .state-select {
    background: #f8f9fa;
    border-color: #e2e8f0;
    font-size: 12px;
}

/* Location Actions */
.location-actions {
    display: flex;
    gap: 10px;
    margin-top: 20px;
    padding-top: 15px;
    border-top: 1px solid #e2e8f0;
    flex-wrap: wrap;
}

.sublocation-btn {
    color: #4299e1;
    border-color: #4299e1;
}

.sublocation-btn:hover {
    background: #4299e1;
    color: white;
}

.save-btn {
    background: #48bb78;
    border-color: #48bb78;
    color: white;
}

.save-btn:hover {
    background: #38a169;
    border-color: #38a169;
}

.save-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.reset-btn {
    color: #ed8936;
    border-color: #ed8936;
}

.reset-btn:hover {
    background: #ed8936;
    color: white;
}

.cancel-btn {
    color: #f56565;
    border-color: #f56565;
}

.cancel-btn:hover {
    background: #f56565;
    color: white;
}

.audit-btn {
    color: #718096;
    border-color: #cbd5e0;
}

.audit-btn:hover {
    background: #edf2f7;
    border-color: #a0aec0;
}

/* Sublocations Section */
.sublocations-section {
    margin-top: 15px;
}

.sublocations-container {
    background: #f8fafc;
    border-radius: 8px;
    padding: 20px;
    border: 1px solid #e2e8f0;
}

.sublocations-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
}

.sublocations-title {
    font-size: 16px;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
}

.add-sublocation-btn {
    color: #667eea;
    border-color: #667eea;
}

.add-sublocation-btn:hover {
    background: #667eea;
    color: white;
}

/* Sublocations List */
.sublocations-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.sublocation-item {
    display: flex;
    align-items: center;
    gap: 15px;
    padding: 10px 15px;
    background: white;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    transition: all 0.2s ease;
}

.sublocation-item:hover {
    border-color: #cbd5e0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.sublocation-number {
    min-width: 50px;
}

.sublocation-index {
    display: inline-block;
    width: 35px;
    height: 35px;
    line-height: 35px;
    text-align: center;
    background: #667eea;
    color: white;
    border-radius: 50%;
    font-size: 12px;
    font-weight: bold;
}

.sublocation-name {
    flex: 2;
    min-width: 250px;
}

.sublocation-name-input {
    background: #fffef0;
    border-color: #e2e8f0;
    font-size: 12px;
    font-weight: bold;
}

.sublocation-type {
    flex: 1;
    min-width: 120px;
}

.sublocation-type-select {
    background: #f8f9fa;
    border-color: #e2e8f0;
    font-size: 12px;
}

.sublocation-actions {
    min-width: 80px;
}

.save-sublocation-btn {
    background: #48bb78;
    border-color: #48bb78;
    color: white;
    font-size: 12px;
    padding: 5px 10px;
}

.save-sublocation-btn:hover {
    background: #38a169;
    border-color: #38a169;
}

.save-sublocation-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.no-sublocations {
    text-align: center;
    padding: 20px;
    color: #a0aec0;
}

/* Table Footer */
.table-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 0;
    margin-top: 15px;
    border-top: 1px solid #e2e8f0;
}

.pagination-info {
    color: #718096;
    font-size: 0.85rem;
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 40px 20px;
    border-radius: 8px;
    background: #f8fafc;
    border: 1px dashed #e2e8f0;
}

.empty-state-icon {
    color: #a0aec0;
    margin-bottom: 15px;
}

.empty-state h3 {
    color: #2d3748;
    margin-bottom: 8px;
    font-weight: 600;
}

.empty-state p {
    color: #718096;
    margin-bottom: 15px;
}

.empty-state-actions {
    display: flex;
    gap: 10px;
    justify-content: center;
    flex-wrap: wrap;
}

/* Audit Trail Modal Styles */
.audit-summary {
    padding-bottom: 10px;
    border-bottom: 1px solid #e2e8f0;
}

.audit-timeline {
    max-height: 500px;
    overflow-y: auto;
}

.audit-timeline::-webkit-scrollbar {
    width: 6px;
}

.audit-timeline::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.audit-timeline::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 3px;
}

.audit-timeline::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
}

.audit-item {
    display: flex;
    gap: 15px;
    padding: 15px;
    border-bottom: 1px solid #e2e8f0;
    transition: background-color 0.2s ease;
}

.audit-item:hover {
    background-color: #f8fafc;
}

.audit-icon {
    flex-shrink: 0;
    padding-top: 3px;
    min-width: 30px;
}

.audit-content {
    flex: 1;
}

.audit-header {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    margin-bottom: 5px;
    align-items: center;
}

.audit-operation {
    font-size: 14px;
    color: #2d3748;
}

.audit-user {
    font-size: 13px;
    color: #4a5568;
}

.audit-time {
    font-size: 12px;
    color: #718096;
}

.audit-description {
    color: #2d3748;
    font-size: 13px;
    margin-bottom: 5px;
}

.audit-changes {
    background: #f7fafc;
    padding: 10px;
    border-radius: 5px;
    margin: 5px 0;
}

.change-list {
    list-style: none;
    padding: 0;
    margin: 5px 0 0;
}

.change-list li {
    font-size: 12px;
    padding: 2px 0;
    color: #4a5568;
}

.old-value {
    color: #e53e3e;
    text-decoration: line-through;
    margin: 0 5px;
}

.new-value {
    color: #38a169;
    margin: 0 5px;
}

.audit-ip {
    margin-top: 3px;
}

/* Responsive Design */
@media (max-width: 1200px) {
    .location-details-row {
        gap: 10px;
    }
    
    .detail-field {
        min-width: 120px;
    }
    
    .detail-field.wide-field {
        min-width: 250px;
    }
}

@media (max-width: 768px) {
    .modern-card {
        padding: 15px;
    }
    
    .header-section {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
    }
    
    .action-buttons {
        width: 100%;
    }
    
    .action-btn {
        flex: 1;
        justify-content: center;
    }
    
    .location-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
    }
    
    .location-actions {
        flex-wrap: wrap;
    }
    
    .sublocation-item {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
    }
    
    .sublocation-name,
    .sublocation-type,
    .sublocation-actions {
        width: 100%;
    }
}

@media (max-width: 480px) {
    .action-buttons {
        flex-direction: column;
    }
    
    .location-actions {
        flex-direction: column;
    }
    
    .action-btn {
        width: 100%;
        justify-content: center;
    }
}
</style>


// WEBPACK FOOTER //
// src/components/Locations/ListLocations.vue